import type { Deployment } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { DeploymentStatus } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { cn } from '@sunstone/ui';
import { Link } from 'react-router';

// ─── Status → Tailwind class ──────────────────────────────────────────────────

const STATUS_CLASS: Partial<Record<DeploymentStatus, string>> = {
  [DeploymentStatus.ACTIVE]: 'bg-[#00D4A4]',
  [DeploymentStatus.PROVISIONED]: 'bg-[#00D4A4]',
  [DeploymentStatus.PROVISIONING]: 'bg-indigo-500',
  [DeploymentStatus.AWAITING_BOOTSTRAP]: 'bg-indigo-500',
  [DeploymentStatus.CREATED]: 'bg-indigo-500',
  [DeploymentStatus.MAINTENANCE]: 'bg-amber-500',
  [DeploymentStatus.MANAGEMENT_BLOCKED]: 'bg-amber-500',
  [DeploymentStatus.BOOTSTRAP_FAILED]: 'bg-rose-500',
  [DeploymentStatus.BOOTSTRAP_TIMEOUT]: 'bg-rose-500',
  [DeploymentStatus.INACTIVE]: 'bg-slate-600',
  [DeploymentStatus.DEPROVISIONING]: 'bg-slate-600',
};

const STATUS_LABEL: Partial<Record<DeploymentStatus, string>> = {
  [DeploymentStatus.ACTIVE]: 'Active',
  [DeploymentStatus.PROVISIONED]: 'Provisioned',
  [DeploymentStatus.PROVISIONING]: 'Provisioning',
  [DeploymentStatus.AWAITING_BOOTSTRAP]: 'Awaiting bootstrap',
  [DeploymentStatus.CREATED]: 'Created',
  [DeploymentStatus.MAINTENANCE]: 'Maintenance',
  [DeploymentStatus.MANAGEMENT_BLOCKED]: 'Management blocked',
  [DeploymentStatus.BOOTSTRAP_FAILED]: 'Bootstrap failed',
  [DeploymentStatus.BOOTSTRAP_TIMEOUT]: 'Bootstrap timeout',
  [DeploymentStatus.INACTIVE]: 'Inactive',
  [DeploymentStatus.DEPROVISIONING]: 'Deprovisioning',
};

const PULSE_STATUSES = new Set([DeploymentStatus.ACTIVE, DeploymentStatus.PROVISIONED]);
const ALERT_STATUSES = new Set([
  DeploymentStatus.BOOTSTRAP_FAILED,
  DeploymentStatus.BOOTSTRAP_TIMEOUT,
  DeploymentStatus.MANAGEMENT_BLOCKED,
]);
const SPIN_STATUSES = new Set([
  DeploymentStatus.PROVISIONING,
  DeploymentStatus.AWAITING_BOOTSTRAP,
  DeploymentStatus.CREATED,
]);

function classFor(status: DeploymentStatus) {
  return STATUS_CLASS[status] ?? 'bg-slate-600';
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  const items = [
    { cls: 'bg-[#00D4A4]', label: 'Active' },
    { cls: 'bg-indigo-500', label: 'Provisioning' },
    { cls: 'bg-amber-500', label: 'Maintenance' },
    { cls: 'bg-rose-500', label: 'Failed' },
    { cls: 'bg-slate-600', label: 'Inactive' },
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {items.map(({ cls, label }) => (
        <div key={label} className="flex items-center gap-1.5">
          <span className={`inline-block rounded-full w-[7px] h-[7px] flex-shrink-0 ${cls}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Single deployment node ────────────────────────────────────────────────────

function DeploymentNode({ deployment }: { deployment: Deployment }) {
  const colorClass = classFor(deployment.status);
  const statusLabel = STATUS_LABEL[deployment.status] ?? 'Unknown';
  const isPulsing = PULSE_STATUSES.has(deployment.status);
  const isAlert = ALERT_STATUSES.has(deployment.status);
  const isSpin = SPIN_STATUSES.has(deployment.status);
  const name = deployment.displayName || deployment.region || deployment.id.slice(-8);

  const href = `/tenants/${deployment.tenantId}/deployments/${deployment.id}`;

  return (
    <Link
      to={href}
      title={`${name} · ${statusLabel}`}
      className="group flex flex-col items-center gap-2 min-w-[60px]"
    >
      <div className="relative flex items-center justify-center" style={{ width: 22, height: 22 }}>
        {isAlert && (
          <span className={`absolute inset-0 rounded-full animate-ping opacity-30 ${colorClass}`} />
        )}
        <span
          className={cn(
            'rounded-full transition-transform group-hover:scale-125',
            isPulsing && 'animate-pulse',
            isSpin && 'animate-spin',
            colorClass,
          )}
          style={{ width: 14, height: 14 }}
        />
      </div>
      <div className="text-center space-y-0.5">
        <p className="text-xs font-medium truncate max-w-[72px] group-hover:text-foreground transition-colors">
          {name}
        </p>
        <p className="text-[10px] text-muted-foreground">{statusLabel}</p>
      </div>
    </Link>
  );
}

// ─── Fleet Pulse ──────────────────────────────────────────────────────────────

interface FleetPulseProps {
  deployments: Deployment[];
  isLoading: boolean;
  totalDeploymentCount?: number;
}

export function FleetPulse({ deployments, isLoading, totalDeploymentCount }: FleetPulseProps) {
  const shown = deployments.length;
  const total = totalDeploymentCount ?? shown;
  const isTruncated = total > shown;

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
            Fleet Pulse
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isLoading ? (
              'Loading…'
            ) : isTruncated ? (
              <>
                20 most recent of {total} deployments
                {' · '}
                <Link to="/deployments" className="text-primary hover:underline underline-offset-2">
                  View all
                </Link>
              </>
            ) : (
              `${total} deployment${total !== 1 ? 's' : ''}`
            )}
          </p>
        </div>
        <Legend />
      </div>

      {isLoading ? (
        <div className="flex gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <div className="h-3.5 w-3.5 rounded-full animate-pulse bg-muted" />
              <div className="h-2 w-10 rounded animate-pulse bg-muted" />
            </div>
          ))}
        </div>
      ) : deployments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No deployments yet.{' '}
          <Link to="/deployments/new" className="text-primary hover:underline underline-offset-4">
            Create one →
          </Link>
        </p>
      ) : (
        <div className="flex flex-wrap gap-5 overflow-x-auto pb-1">
          {deployments.map((d) => (
            <DeploymentNode key={d.id} deployment={d} />
          ))}
        </div>
      )}
    </div>
  );
}
